// Company-name tokenizer for bank-narrative fuzzy matching.
//
// Extracted from paymentMatcher.ts (the deprecated v1 matcher) because this is
// the only part of it live code still uses (mbank-sync builds counterparty name
// tokens for MatchableInvoice with it). Finding m2.
//
// Normalization rules:
//   - strip Polish diacritics (bank narratives often drop them: LOWISKO vs Łowisko)
//   - remove legal-form suffixes (sp. z o.o., S.A., sp.j., sp.k., s.c., full forms)
//     so they don't drag jaccard similarity against short merchant tags
//   - replace every non-letter/digit/whitespace char with a space
//   - split on whitespace, drop tokens shorter than 2 chars

/**
 * Strip Polish diacritics so bank narratives that drop them (`LOWISKO`)
 * still match company names that don't (`Łowisko`). NFD splits `ó`
 * into `o` + combining accent which we then drop; `ł` doesn't
 * decompose so we map it explicitly.
 */
function stripDiacritics(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L');
}

export function tokenizeName(name: string): string[] {
  // JS regex `\b` keys off `\w = [A-Za-z0-9_]` — Polish chars like
  // `ą`, `ć`, `ł` aren't word chars, so `\b` after `odpowiedzialnością`
  // never matches. Use explicit start/whitespace lookbehind +
  // whitespace/end/punct lookahead instead.
  const START = '(?<=^|\\s)';
  const END = '(?=\\s|$|[.,;:/])';
  return stripDiacritics(name)
    .toLowerCase()
    // Full LLC form: "spółka z ograniczoną odpowiedzialnością"
    .replace(new RegExp(`${START}sp(?:ółka|olka)\\s+z\\s+ograniczon[aą]\\s+odpowiedzialno[sś]ci[aą]${END}`, 'giu'), '')
    // Abbreviated LLC form: "sp. z o.o.", "spółka z o.o.", "s.p. z o.o."
    .replace(new RegExp(`${START}s\\.?\\s*p(?:ółka|olka|\\.)?\\s*z\\s*o\\.?o\\.?${END}`, 'giu'), '')
    // Joint-stock: "spółka akcyjna" / "s.a."
    .replace(new RegExp(`${START}sp(?:ółka|olka)\\s+akcyjna${END}`, 'giu'), '')
    .replace(new RegExp(`${START}s\\.\\s*a\\.${END}`, 'giu'), '')
    // General partnership: "spółka jawna" / "sp.j."
    .replace(new RegExp(`${START}sp(?:ółka|olka|\\.)?\\s*j\\.?${END}`, 'giu'), '')
    // Limited partnership: "spółka komandytowa" / "sp.k."
    .replace(new RegExp(`${START}sp(?:ółka|olka)\\s+komandytowa${END}`, 'giu'), '')
    .replace(new RegExp(`${START}sp\\.?\\s*k\\.?${END}`, 'giu'), '')
    // Civil partnership: "spółka cywilna" / "s.c."
    .replace(new RegExp(`${START}sp(?:ółka|olka)\\s+cywilna${END}`, 'giu'), '')
    .replace(new RegExp(`${START}s\\.\\s*c\\.${END}`, 'giu'), '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((t) => t.length >= 2);
}
